<template>
  <transition name="slide-down">
    <div
      v-if="isVisible"
      class="network-status-banner"
      :class="bannerClass"
      role="status"
      aria-live="polite"
    >
      <div class="banner-content">
        <!-- Icon -->
        <div class="banner-icon-wrapper">
          <!-- Offline Icon -->
          <svg
            v-if="status === 'offline'"
            xmlns="http://www.w3.org/2000/svg"
            class="banner-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line x1="1" y1="1" x2="23" y2="23"></line>
            <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55"></path>
            <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39"></path>
            <path d="M10.71 5.05A16 16 0 0 1 22.58 9"></path>
            <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88"></path>
            <path d="M8.53 16.11a6 6 0 0 1 6.95 0"></path>
            <line x1="12" y1="20" x2="12.01" y2="20"></line>
          </svg>

          <!-- Slow Connection Icon -->
          <svg
            v-else-if="status === 'slow'"
            xmlns="http://www.w3.org/2000/svg"
            class="banner-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>

          <!-- Restored Icon -->
          <svg
            v-else-if="status === 'restored'"
            xmlns="http://www.w3.org/2000/svg"
            class="banner-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
        </div>

        <!-- Text Message -->
        <div class="banner-text">
          <span class="banner-title">{{ bannerTitle }}</span>
          <span class="banner-desc">{{ bannerMessage }}</span>
          <span v-if="speedDetails && status === 'slow'" class="banner-speed-badge">
            {{ speedDetails }}
          </span>
        </div>

        <!-- Actions -->
        <div class="banner-actions">
          <button
            v-if="status === 'offline'"
            type="button"
            class="banner-btn banner-btn-retry"
            :disabled="isChecking"
            @click="checkConnectionManual"
          >
            <span v-if="isChecking" class="spinner-inline"></span>
            {{ isChecking ? 'Checking...' : 'Retry Now' }}
          </button>

          <button
            type="button"
            class="banner-btn banner-btn-dismiss"
            @click="dismissBanner"
            title="Dismiss notification"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'NetworkStatusBanner',
  data () {
    return {
      status: 'online', // 'online' | 'slow' | 'offline' | 'restored'
      isChecking: false,
      dismissedSlow: false,
      speedDetails: '',
      restoredTimer: null,
      periodicCheckTimer: null,
      lastOnlineState: navigator.onLine
    }
  },
  computed: {
    isVisible () {
      if (this.status === 'offline') return true
      if (this.status === 'restored') return true
      if (this.status === 'slow' && !this.dismissedSlow) return true
      return false
    },
    bannerClass () {
      return {
        'banner-offline': this.status === 'offline',
        'banner-slow': this.status === 'slow',
        'banner-restored': this.status === 'restored'
      }
    },
    bannerTitle () {
      if (this.status === 'offline') return 'You are offline'
      if (this.status === 'slow') return 'Slow connection detected'
      if (this.status === 'restored') return 'Back online!'
      return ''
    },
    bannerMessage () {
      if (this.status === 'offline') {
        return 'Changes and updates may not save to NIMBLE until your connection is restored.'
      }
      if (this.status === 'slow') {
        return 'Data syncing, reports, and actions in NIMBLE may take a moment longer to process.'
      }
      if (this.status === 'restored') {
        return 'Your connection to NIMBLE has been restored.'
      }
      return ''
    }
  },
  mounted () {
    // Initial evaluation
    this.evaluateNetwork()

    // Browser Online/Offline event listeners
    window.addEventListener('online', this.handleOnlineEvent)
    window.addEventListener('offline', this.handleOfflineEvent)

    // Network Information API listener (supported in Chromium/Edge/Android)
    const conn = this.getConnection()
    if (conn) {
      conn.addEventListener('change', this.evaluateNetwork)
    }

    // Background latency check every 45s (fallback for browsers without NetworkInfo API)
    this.periodicCheckTimer = setInterval(this.checkLatency, 45000)
  },
  beforeDestroy () {
    window.removeEventListener('online', this.handleOnlineEvent)
    window.removeEventListener('offline', this.handleOfflineEvent)

    const conn = this.getConnection()
    if (conn) {
      conn.removeEventListener('change', this.evaluateNetwork)
    }

    if (this.restoredTimer) clearTimeout(this.restoredTimer)
    if (this.periodicCheckTimer) clearInterval(this.periodicCheckTimer)
  },
  methods: {
    getConnection () {
      return (
        navigator.connection ||
        navigator.mozConnection ||
        navigator.webkitConnection ||
        null
      )
    },
    handleOfflineEvent () {
      this.lastOnlineState = false
      this.status = 'offline'
      this.dismissedSlow = false
      if (this.restoredTimer) clearTimeout(this.restoredTimer)
    },
    handleOnlineEvent () {
      if (!this.lastOnlineState || this.status === 'offline') {
        this.triggerRestored()
      } else {
        this.evaluateNetwork()
      }
    },
    triggerRestored () {
      this.status = 'restored'
      this.lastOnlineState = true
      if (this.restoredTimer) clearTimeout(this.restoredTimer)
      this.restoredTimer = setTimeout(() => {
        this.status = 'online'
      }, 3000)
    },
    evaluateNetwork () {
      if (!navigator.onLine) {
        this.status = 'offline'
        this.lastOnlineState = false
        if (this.restoredTimer) clearTimeout(this.restoredTimer)
        return
      }

      this.lastOnlineState = true

      // If displaying restored banner, let the 3s timeout finish
      if (this.status === 'restored') {
        return
      }

      const conn = this.getConnection()
      if (conn) {
        const { effectiveType, rtt, downlink, saveData } = conn
        const isSlowType = effectiveType === 'slow-2g' || effectiveType === '2g'
        const isHighLatency = rtt && rtt >= 1600
        const isLowBandwidth = downlink && downlink < 0.65

        if (isSlowType || isHighLatency || isLowBandwidth || saveData) {
          this.status = 'slow'
          const details = []
          if (effectiveType) details.push(effectiveType.toUpperCase())
          if (downlink) details.push(`${downlink} Mbps`)
          if (rtt) details.push(`${rtt}ms latency`)
          this.speedDetails = details.join(' • ')
          return
        }
      }

      // Normal online state
      this.status = 'online'
    },
    async pingInternet () {
      if (!navigator.onLine) {
        return { ok: false, duration: 0 }
      }

      const startTime = performance.now()
      const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
      const timeoutId = controller ? setTimeout(() => controller.abort(), 3500) : null

      try {
        // Fast lightweight public ping probe (Cloudflare 1.1.1.1)
        await fetch(`https://1.1.1.1/cdn-cgi/trace?_t=${Date.now()}`, {
          method: 'GET',
          cache: 'no-store',
          signal: controller ? controller.signal : undefined
        })
        if (timeoutId) clearTimeout(timeoutId)
        const duration = performance.now() - startTime
        return { ok: true, duration }
      } catch (err) {
        if (timeoutId) clearTimeout(timeoutId)
        // Secondary fallback probe (Google) with no-cors
        try {
          const fbController = typeof AbortController !== 'undefined' ? new AbortController() : null
          const fbTimeout = fbController ? setTimeout(() => fbController.abort(), 2500) : null
          await fetch(`https://www.google.com/favicon.ico?_t=${Date.now()}`, {
            method: 'HEAD',
            mode: 'no-cors',
            cache: 'no-store',
            signal: fbController ? fbController.signal : undefined
          })
          if (fbTimeout) clearTimeout(fbTimeout)
          const duration = performance.now() - startTime
          return { ok: true, duration }
        } catch (e) {
          return { ok: false, duration: 0 }
        }
      }
    },
    async checkLatency () {
      // Don't test latency if explicitly offline or currently checking
      if (!navigator.onLine || this.isChecking || this.status === 'offline') return

      const result = await this.pingInternet()
      if (!result.ok) {
        this.status = 'offline'
        this.lastOnlineState = false
        return
      }

      // If latency exceeds 2500ms, mark as slow connection
      if (result.duration > 2500 && this.status !== 'offline') {
        this.status = 'slow'
        this.speedDetails = `~${Math.round(result.duration)}ms ping`
      } else if (this.status === 'slow' && result.duration <= 1500) {
        // Connection recovered
        this.status = 'online'
        this.dismissedSlow = false
      }
    },
    async checkConnectionManual () {
      if (!navigator.onLine) {
        this.status = 'offline'
        this.lastOnlineState = false
        return
      }

      this.isChecking = true
      const result = await this.pingInternet()
      this.isChecking = false

      if (!result.ok) {
        this.status = 'offline'
        this.lastOnlineState = false
        return
      }

      if (result.duration > 2200) {
        this.status = 'slow'
        this.speedDetails = `~${Math.round(result.duration)}ms ping`
      } else {
        this.triggerRestored()
      }
    },
    dismissBanner () {
      if (this.restoredTimer) clearTimeout(this.restoredTimer)
      this.status = 'online'
      this.dismissedSlow = true
    }
  }
}
</script>

<style scoped>
/* Slide Down Transition */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1);
}
.slide-down-enter {
  transform: translateY(-100%);
  opacity: 0;
}
.slide-down-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

/* Banner Container */
.network-status-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 999999;
  padding: 10px 16px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
  display: flex;
  justify-content: center;
  align-items: center;
}

.banner-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 1200px;
  gap: 12px;
}

/* Theme Variants */
.banner-offline {
  background: linear-gradient(135deg, #dc2626, #b91c1c);
  color: #ffffff;
  border-bottom: 1px solid #991b1b;
}

.banner-slow {
  background: linear-gradient(135deg, #d97706, #b45309);
  color: #ffffff;
  border-bottom: 1px solid #92400e;
}

.banner-restored {
  background: linear-gradient(135deg, #059669, #047857);
  color: #ffffff;
  border-bottom: 1px solid #065f46;
}

/* Icon */
.banner-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.banner-icon {
  width: 22px;
  height: 22px;
  stroke: currentColor;
}

/* Text */
.banner-text {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  flex-grow: 1;
  font-size: 13.5px;
  line-height: 1.4;
}

.banner-title {
  font-weight: 700;
  letter-spacing: 0.2px;
}

.banner-desc {
  opacity: 0.92;
  font-weight: 400;
}

.banner-speed-badge {
  display: inline-block;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.25);
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.3px;
}

/* Actions */
.banner-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.banner-btn {
  border: none;
  cursor: pointer;
  padding: 5px 12px;
  border-radius: 6px;
  font-size: 12.5px;
  font-weight: 600;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.banner-btn-retry {
  background-color: #ffffff;
  color: #b91c1c;
}
.banner-btn-retry:hover {
  background-color: #f3f4f6;
  transform: translateY(-1px);
}
.banner-btn-retry:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.banner-btn-dismiss {
  background: rgba(255, 255, 255, 0.18);
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.35);
}
.banner-btn-dismiss:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Inline Spinner */
.spinner-inline {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid rgba(185, 28, 28, 0.3);
  border-top-color: #b91c1c;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 640px) {
  .banner-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  .banner-actions {
    align-self: flex-end;
  }
}
</style>
